// src/RecipeDB/RecipeForm.js
import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { addRecipe } from './recipeService.js';
import { db } from '../config/firebase';
import { doc, updateDoc, arrayUnion } from 'firebase/firestore';
import { useAuth } from '../Auth/AuthContext';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getAuth } from 'firebase/auth';
import { v4 as uuidv4 } from 'uuid';
import { useToast } from '../context/ToastContext';
import { compressImage } from '../utils/compressImage';
import { usePlan } from '../context/PlanContext';
import RecipeImportModal from '../Monetization/RecipeImportModal';
import { API } from '../config/api';
import './RecipeFormWizard.css';
const STEPS = ['Lo básico', 'Ingredientes', 'Instrucciones', 'Detalles'];

const EMPTY = () => ({
  id: uuidv4(),
  nombre: '',
  ingredientes: [{ nombre: '', cantidad: '', unidad: '' }],
  instrucciones: [''],
  tiempoEstimado: '',
  dificultad: '',
  privacidad: 'publica',
});

const RecipeForm = () => {
  const { userId } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const { isPremium, limits, canUseFeature, incrementUsage, showUpgradeModal } = usePlan();

  const [step, setStep]                 = useState(0);
  const [recipe, setRecipe]             = useState(EMPTY);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [imageFiles, setImageFiles]     = useState([]);
  const [videoFile, setVideoFile]       = useState(null);
  const [videoPreview, setVideoPreview] = useState('');
  const [uploading, setUploading]       = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);

  // ── Audio / IA ─────────────────────────────────────────────────────
  const mediaRecorderRef = useRef(null);
  const audioChunksRef   = useRef([]);
  const [isRecording, setIsRecording]       = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [transcription, setTranscription]   = useState('');

  // ── Imágenes ───────────────────────────────────────────────────────
  const handleImageAdd = (e, slotIdx) => {
    const file = e.target.files[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    setImagePreviews(prev => { const a = [...prev]; a[slotIdx] = url; return a; });
    setImageFiles(prev => { const a = [...prev]; a[slotIdx] = file; return a; });
  };

  const removeImage = (idx) => {
    setImagePreviews(prev => prev.filter((_, i) => i !== idx));
    setImageFiles(prev => prev.filter((_, i) => i !== idx));
  };

  // ── Vídeo ─────────────────────────────────────────────────────
  const handleVideoAdd = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    // Límite: 500 MB
    if (file.size > 500 * 1024 * 1024) {
      showToast('El vídeo no puede superar los 500 MB', 'error');
      return;
    }
    setVideoFile(file);
    setVideoPreview(URL.createObjectURL(file));
  };

  const removeVideo = () => {
    setVideoFile(null);
    setVideoPreview('');
  };

  // ── Ingredientes ───────────────────────────────────────────────────
  const updateIng = (idx, field, val) => {
    const list = [...recipe.ingredientes];
    list[idx] = { ...list[idx], [field]: val };
    setRecipe(r => ({ ...r, ingredientes: list }));
  };
  const addIng    = () => setRecipe(r => ({ ...r, ingredientes: [...r.ingredientes, { nombre: '', cantidad: '', unidad: '' }] }));
  const removeIng = (idx) => setRecipe(r => ({ ...r, ingredientes: r.ingredientes.filter((_, i) => i !== idx) }));

  // ── Grabación de audio ─────────────────────────────────────────────
  const startRecording = async () => {    // Gate: audio trial (1 lifetime for basic)
    if (!canUseFeature('audio')) {
      showUpgradeModal('audio');
      return;
    }    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      mediaRecorderRef.current = mr;
      audioChunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        await sendAudioToAI(blob);
      };
      mr.start();
      setIsRecording(true);
    } catch {
      showToast('No se pudo acceder al micrófono', 'error');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const sendAudioToAI = async (audioBlob) => {
    setIsTranscribing(true);
    try {
      const idToken = await getAuth().currentUser.getIdToken();
      const base64 = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result.split(',')[1]);
        reader.readAsDataURL(audioBlob);
      });

      const res = await fetch(API.parseRecipeFromAudio, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({ audio: base64, mimeType: audioBlob.type }),
      });

      const data = await res.json();
      if (!data.success) throw new Error(data.error);

      // Sanear ingredientes al formato exacto de la BD
      const ingredientes = (data.recipe.ingredientes || [])
        .filter(ing => ing.nombre?.trim())
        .map(ing => ({
          nombre:   (ing.nombre   || '').trim(),
          cantidad: String(ing.cantidad || '').trim(),
          unidad:   (ing.unidad   || '').trim(),
        }));

      // Sanear instrucciones: un paso por elemento, sin vacíos
      const instrucciones = (data.recipe.instrucciones || [])
        .flatMap(paso => paso.split(/\.\s+|\n+/).map(s => s.trim()).filter(Boolean));

      setTranscription(data.transcription);
      setRecipe(prev => ({
        ...prev,
        nombre:         data.recipe.nombre?.trim()        || prev.nombre,
        ingredientes:   ingredientes.length ? ingredientes : prev.ingredientes,
        instrucciones:  instrucciones.length ? instrucciones : prev.instrucciones,
        dificultad:     data.recipe.dificultad             || prev.dificultad,
        tiempoEstimado: data.recipe.tiempoEstimado         || prev.tiempoEstimado,
      }));

      setStep(0);
      await incrementUsage('audio');
      showToast('¡Receta detectada! Revisa y completa los campos 🎤', 'success');
    } catch (e) {
      console.error(e);
      showToast('Error al procesar el audio. Inténtalo de nuevo.', 'error');
    } finally {
      setIsTranscribing(false);
    }
  };

  // ── Importar receta (texto / foto) ──────────────────────────────────
  const handleImport = (data) => {
    const ingredientes = Array.isArray(data.ingredientes)
      ? data.ingredientes.map(ing =>
          typeof ing === 'object'
            ? { nombre: (ing.nombre || '').trim(), cantidad: String(ing.cantidad || ''), unidad: (ing.unidad || '').trim() }
            : { nombre: String(ing).trim(), cantidad: '', unidad: '' }
        ).filter(ing => ing.nombre)
      : [];
    const instrucciones = Array.isArray(data.instrucciones)
      ? data.instrucciones
          .flatMap(paso => paso.split(/\.\s+|\n+/).map(s => s.trim()).filter(Boolean))
      : [];
    setRecipe(prev => ({
      ...prev,
      nombre:         data.nombre?.trim()        || prev.nombre,
      ingredientes:   ingredientes.length ? ingredientes : prev.ingredientes,
      instrucciones:  instrucciones.length ? instrucciones : prev.instrucciones,
      dificultad:     data.dificultad             || prev.dificultad,
      tiempoEstimado: data.tiempoEstimado         || prev.tiempoEstimado,
    }));
    setShowImportModal(false);
    setStep(0);
    showToast('¡Receta importada! Revisa y completa los campos 📥', 'success');
  };

  // ── Instrucciones ──────────────────────────────────────────────────
  const updateInst = (idx, val) => {
    const list = [...recipe.instrucciones];
    list[idx] = val;
    setRecipe(r => ({ ...r, instrucciones: list }));
  };
  const addInst    = () => setRecipe(r => ({ ...r, instrucciones: [...r.instrucciones, ''] }));
  const removeInst = (idx) => setRecipe(r => ({ ...r, instrucciones: r.instrucciones.filter((_, i) => i !== idx) }));

  // ── Validación ─────────────────────────────────────────────────────
  const canGoNext = () => {
    if (step === 0) return recipe.nombre.trim().length > 0;
    if (step === 1) return recipe.ingredientes[0]?.nombre?.trim().length > 0;
    if (step === 2) return recipe.instrucciones[0]?.trim().length > 0;
    return true;
  };

  // ── Submit ─────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!userId) return;
    if (!recipe.nombre.trim()) { showToast('El nombre es obligatorio', 'error'); return; }
    setUploading(true);
    try {
      const storage = getStorage();
      const imageUrls = await Promise.all(
        imageFiles.map(async (file, i) => {
          const compressed = await compressImage(file);
          const storageRef = ref(storage, `recetas/${Date.now()}_${i}_${file.name}`);
          await uploadBytes(storageRef, compressed);
          return getDownloadURL(storageRef);
        })
      );

      // Subir vídeo si existe (solo Premium)
      let videoUrl = '';
      if (videoFile && isPremium) {
        const videoRef = ref(storage, `recetas/videos/${Date.now()}_${videoFile.name}`);
        await uploadBytes(videoRef, videoFile);
        videoUrl = await getDownloadURL(videoRef);
      }

      await addRecipe({
        ...recipe,
        userId,
        fuente: 'usuario',
        imagen: imageUrls[0] || '',
        imagenes: imageUrls,
        ...(videoUrl ? { videoUrl } : {}),
      });

      await updateDoc(doc(db, 'users', userId), {
        recetasCompartidas: arrayUnion(recipe.id),
      });

      showToast('¡Receta publicada con éxito! 🎉', 'success');
      navigate('/community');
    } catch (err) {
      console.error(err);
      showToast('Error al publicar la receta', 'error');
    } finally {
      setUploading(false);
    }
  };

  // ── Resize dinámico para inputs cortos ────────────────────────────
  const dynWidth = (e, min = 32) => {
    const len = Math.max(
      (e.target.value || '').length,
      (e.target.placeholder || '').length
    );
    e.target.style.width = Math.max(min, len * 8 + 16) + 'px';
  };

  // ── Render pasos ───────────────────────────────────────────────────
  const renderStep = () => {
    switch (step) {
      case 0: return (
        <div className="rfw-step-content" key="s0">
          <h2 className="rfw-step-title">¿Cómo se llama tu receta?</h2>
          <input
            className="rfw-input-big"
            placeholder="Ej: Tortilla de patatas casera"
            value={recipe.nombre}
            onChange={e => setRecipe(r => ({ ...r, nombre: e.target.value }))}
            autoFocus
          />

          <label className="rfw-label">📸 Fotos</label>
          <div className="rfw-images-row">
            {[0,1,2,3,4].map(slotIdx => {
              const preview = imagePreviews[slotIdx];
              const isLocked = !isPremium && slotIdx >= limits.maxRecipePhotos;

              if (preview) {
                return (
                  <div key={slotIdx} className="rfw-img-thumb">
                    <img src={preview} alt={`img-${slotIdx}`} />
                    <button className="rfw-img-remove" onClick={() => removeImage(slotIdx)}>✕</button>
                  </div>
                );
              }
              if (isLocked) {
                return (
                  <div key={slotIdx} className="rfw-img-slot-locked" onClick={() => showUpgradeModal('photo')}>
                    <span className="rfw-img-lock-icon">🔒</span>
                  </div>
                );
              }
              return (
                <label key={slotIdx} className="rfw-img-add">
                  <span>+</span>
                  <input type="file" accept="image/*" onChange={e => handleImageAdd(e, slotIdx)} hidden />
                </label>
              );
            })}
          </div>

          {/* ── Vídeo (Premium) ── */}
          <label className="rfw-label">🎬 Vídeo de la receta</label>
          {!isPremium ? (
            <div className="rfw-video-locked" onClick={() => showUpgradeModal('video')}>
              <span className="rfw-img-lock-icon">🔒</span>
              <span>Sube un vídeo con Premium</span>
            </div>
          ) : videoPreview ? (
            <div className="rfw-video-preview">
              <video src={videoPreview} controls className="rfw-video-player" />
              <button className="rfw-img-remove rfw-video-remove" onClick={removeVideo}>✕ Quitar vídeo</button>
            </div>
          ) : (
            <label className="rfw-video-add">
              <span>📹 Seleccionar vídeo</span>
              <input type="file" accept="video/*" onChange={handleVideoAdd} hidden />
            </label>
          )}
        </div>
      );

      case 1: return (
        <div className="rfw-step-content" key="s1">
          <h2 className="rfw-step-title">¿Qué ingredientes necesitas?</h2>
          <div className="rfw-ingredients-list">
            {recipe.ingredientes.map((ing, i) => (
              <div key={i} className="rfw-ingredient-row">
                <span className="rfw-ing-num">{i + 1}</span>
                <input
                  className="rfw-input rfw-ing-nombre"
                  placeholder="Ingrediente"
                  value={ing.nombre}
                  onChange={e => updateIng(i, 'nombre', e.target.value)}
                />
                <input
                  className="rfw-input rfw-ing-cantidad"
                  placeholder="Cant."
                  value={ing.cantidad}
                  type="number"
                  min="0"
                  onChange={e => { updateIng(i, 'cantidad', e.target.value); dynWidth(e, 42); }}
                  onFocus={e => e.target.select()}
                />
                <input
                  className="rfw-input rfw-ing-unidad"
                  placeholder="Uds"
                  value={ing.unidad}
                  onChange={e => { updateIng(i, 'unidad', e.target.value); dynWidth(e, 42); }}
                />
                {recipe.ingredientes.length > 1 && (
                  <button className="rfw-remove-btn" onClick={() => removeIng(i)}>✕</button>
                )}
              </div>
            ))}
          </div>
          <button className="rfw-add-btn" onClick={addIng}>+ Añadir ingrediente</button>
        </div>
      );

      case 2: return (
        <div className="rfw-step-content" key="s2">
          <h2 className="rfw-step-title">¿Cómo se prepara?</h2>
          <div className="rfw-instructions-list">
            {recipe.instrucciones.map((inst, i) => (
              <div key={i} className="rfw-instruction-row">
                <span className="rfw-step-badge">{i + 1}</span>
                <textarea
                  className="rfw-textarea"
                  placeholder={`Paso ${i + 1}...`}
                  value={inst}
                  onChange={e => updateInst(i, e.target.value)}
                  rows={2}
                  onInput={e => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
                />
                {recipe.instrucciones.length > 1 && (
                  <button className="rfw-remove-btn" onClick={() => removeInst(i)}>✕</button>
                )}
              </div>
            ))}
          </div>
          <button className="rfw-add-btn" onClick={addInst}>+ Añadir paso</button>
        </div>
      );

      case 3: return (
        <div className="rfw-step-content" key="s3">
          <h2 className="rfw-step-title">Últimos detalles</h2>

          <label className="rfw-label">⏱ Tiempo estimado</label>
          <div className="rfw-time-row">
            <input
              className="rfw-input rfw-time-input"
              type="number" min="0" placeholder="0"
              value={recipe.tiempoEstimado}
              onChange={e => setRecipe(r => ({ ...r, tiempoEstimado: Number(e.target.value) }))}
            />
            <span className="rfw-time-unit">minutos</span>
          </div>

          <label className="rfw-label">📊 Dificultad</label>
          <div className="rfw-chips-row">
            {[{ value: 'fácil', icon: '🟢', label: 'Fácil' }, { value: 'media', icon: '🟡', label: 'Media' }, { value: 'difícil', icon: '🔴', label: 'Difícil' }].map(d => (
              <button key={d.value} className={`rfw-chip${recipe.dificultad === d.value ? ' active' : ''}`}
                onClick={() => setRecipe(r => ({ ...r, dificultad: d.value }))}>
                {d.icon} {d.label}
              </button>
            ))}
          </div>

          <label className="rfw-label">🔒 Privacidad</label>
          <div className="rfw-chips-row">
            {[{ value: 'publica', icon: '🌍', label: 'Pública' }, { value: 'seguidores', icon: '👥', label: 'Seguidores' }, { value: 'privada', icon: '🔒', label: 'Solo yo' }].map(p => (
              <button key={p.value} className={`rfw-chip${recipe.privacidad === p.value ? ' active' : ''}`}
                onClick={() => setRecipe(r => ({ ...r, privacidad: p.value }))}>
                {p.icon} {p.label}
              </button>
            ))}
          </div>
        </div>
      );

      default: return null;
    }
  };

  return (
    <div className="rfw-container">

      {/* ── Header con progreso ── */}
      <div className="rfw-header">
        <button className="rfw-back-btn" onClick={() => step > 0 ? setStep(s => s - 1) : navigate(-1)}>←</button>
        <div className="rfw-progress-bar">
          <div className="rfw-progress-fill" style={{ width: `${((step + 1) / STEPS.length) * 100}%` }} />
        </div>
        <span className="rfw-step-counter">{step + 1}/{STEPS.length}</span>
      </div>

      {/* ── Banner estado grabación ── */}
      {(isRecording || isTranscribing) && (
        <div className={`rfw-audio-banner${isRecording ? ' active' : ''}`}>
          {isRecording
            ? <><span className="rfw-pulse" />Grabando... Pulsa ⏹ cuando termines</>
            : '⏳ Procesando con IA...'}
        </div>
      )}

      {/* ── Transcripción resultante ── */}
      {transcription && !isRecording && !isTranscribing && (
        <div className="rfw-transcription">
          <span className="rfw-transcription-label">🎤 Texto detectado:</span>
          <p className="rfw-transcription-text">"{transcription}"</p>
        </div>
      )}

      {/* ── Contenido del paso ── */}
      {renderStep()}

      {/* ── Footer ── */}
      <div className="rfw-footer">
        {/* Botón Importar Receta — visible en pasos 0,1,2 */}
        {step < 3 && (
          <button
            className="rfw-import-btn"
            onClick={() => setShowImportModal(true)}
          >
            📥 Importar
          </button>
        )}

        {/* Botón Audio Receta — visible en pasos 0,1,2 */}
        {step < 3 && (
          <button
            className={`rfw-audio-btn${isRecording ? ' recording' : ''}${isTranscribing ? ' transcribing' : ''}`}
            onClick={isRecording ? stopRecording : startRecording}
            disabled={isTranscribing}
          >
            {isTranscribing ? '⏳ Procesando...' : isRecording ? '⏹ Detener grabación' : '🎤 Audio Receta'}
          </button>
        )}

        {step < STEPS.length - 1 ? (
          <button className="rfw-next-btn" onClick={() => setStep(s => s + 1)} disabled={!canGoNext()}>
            Siguiente →
          </button>
        ) : (
          <button className="rfw-submit-btn" onClick={handleSubmit} disabled={uploading}>
            {uploading ? '⏳ Publicando...' : '🚀 Publicar receta'}
          </button>
        )}
      </div>

      {uploading && (
        <div className="rfw-loader-overlay">
          <div className="rfw-loader"><p>Subiendo y publicando...</p></div>
        </div>
      )}

      {showImportModal && (
        <RecipeImportModal
          onClose={() => setShowImportModal(false)}
          onImport={handleImport}
        />
      )}
    </div>
  );
};

export default RecipeForm;